// src/services/payrollServiceSimple.js
// FIXED: Explicitly select calculation_metadata + better error handling

import { supabase } from './supabase';
import moment from 'moment';

export const payrollServiceSimple = {

  async calculatePayrollSimple(startDate, endDate, company_id) {
    console.log('🚀 Starting SIMPLE payroll calculation:', { startDate, endDate, company_id });
    
    try {
      // Fetch company settings
      const { data: companySettings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', company_id)
        .single();
      
      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      // Get settings
      const overtimeThreshold = companySettings?.payroll_settings?.monthly_overtime_threshold || 195;
      const tier1OvertimeRate = companySettings?.payroll_settings?.overtime_rate || 1.5;
      const tier2OvertimeRate = companySettings?.payroll_settings?.double_overtime_rate || 2.0;
      const tier1Hours = companySettings?.payroll_settings?.overtime_tier_1_hours || 5;
      const holidayRate = companySettings?.payroll_settings?.holiday_rate || 2.0;

      console.log('⚙️ Settings loaded:', {
        overtimeThreshold,
        tier1OvertimeRate,
        tier2OvertimeRate,
        tier1Hours,
        holidayRate
      });

      // Fetch public holidays (both specific dates and recurring)
      // Fetch ALL holidays for this company - all entries in holidays table are treated as public holidays
      const { data: publicHolidays, error: holidaysError } = await supabase
        .from('holidays')
        .select('date, is_recurring, name')
        .eq('company_id', company_id);  // All holidays = public holidays (paid at 2x rate)

      if (holidaysError) {
        console.error('⚠️ Error fetching holidays:', holidaysError);
      }

      // Helper function to check if a date is a public holiday (handles recurring)
      const isPublicHolidayDate = (checkDate) => {
        return (publicHolidays || []).some(holiday => {
          const holidayDate = moment(holiday.date);
          const targetDate = moment(checkDate);

          if (holiday.is_recurring) {
            // For recurring holidays, match month and day (ignore year)
            return holidayDate.month() === targetDate.month() &&
                   holidayDate.date() === targetDate.date();
          } else {
            // For one-time holidays, exact date match
            return holidayDate.format('YYYY-MM-DD') === targetDate.format('YYYY-MM-DD');
          }
        });
      };

      console.log(`📅 Loaded ${(publicHolidays || []).length} public holiday definitions (${(publicHolidays || []).filter(h => h.is_recurring).length} recurring)`);

      // Get attendance data
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('timesheet_daily_records')
        .select(`
          *,
          employees (
            id,
            first_name,
            last_name,
            employee_number,
            position,
            department,
            hourly_rate,
            break_minutes
          )
        `)
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (attendanceError) throw attendanceError;

      console.log(`📊 Loaded ${attendanceData?.length || 0} attendance records`);

      // Get unique employee IDs
      const employeeIds = [...new Set(attendanceData.map(record => record.employee_id))];

      // Get employee data including monthly_overtime_threshold
      const { data: employees, error: employeeError } = await supabase
        .from('employees')
        .select('*, monthly_overtime_threshold')
        .in('id', employeeIds);

      if (employeeError) throw employeeError;

      console.log(`👥 Processing ${employees?.length || 0} employees`);

      const employeeMap = {};
      employees.forEach(emp => {
        employeeMap[emp.id] = emp;
      });

      // Create payroll period
      const period_id = await this.createPayrollPeriod(startDate, endDate, company_id);
      console.log(`📅 Created payroll period: ${period_id}`);

      // Group attendance by employee
      const employeeAttendance = {};
      attendanceData.forEach(record => {
        if (!employeeAttendance[record.employee_id]) {
          employeeAttendance[record.employee_id] = [];
        }
        employeeAttendance[record.employee_id].push(record);
      });

      // Calculate payroll for each employee
      const payrollRecords = await Promise.all(employees.map(async (employee) => {
        const records = employeeAttendance[employee.id] || [];

        // STEP 1: Separate PH hours, regular hours, and leave hours
        let totalRegularHours = 0;
        let totalPHHours = 0;
        let leaveHours = 0;
        let leaveDays = 0;
        let phDaysCount = 0;  // Track PH days separately for lunch deduction

        // FIXED: Count unique dates for working days, not total records
        const uniqueDates = new Set();

        records.forEach(record => {
          const recordDate = record.date;
          const dayOfWeek = moment(recordDate).day(); // 0 = Sunday, 1 = Monday, etc.
          const isPublicHoliday = isPublicHolidayDate(recordDate);  // FIXED: Use helper function
          const isSunday = dayOfWeek === 0;

          // CRITICAL: Handle leave days (SICK LEAVE, LOCAL LEAVE) - count as regular time
          if (record.leave_type === 'SICK LEAVE' || record.leave_type === 'LOCAL LEAVE') {
            // Credit 8 hours per leave day as regular time
            const leaveHoursPerDay = 8;
            totalRegularHours += leaveHoursPerDay;
            leaveHours += leaveHoursPerDay;
            leaveDays++;
            uniqueDates.add(recordDate);
            console.log(`🏥 ${employee.first_name}: ${recordDate} ${record.leave_type} - credited ${leaveHoursPerDay}h as regular time`);
            return; // Skip to next record
          }

          // Skip ABSENT days (cut off)
          if (record.leave_type === 'ABSENT' || record.status === 'absent') {
            console.log(`❌ ${employee.first_name}: ${recordDate} ABSENT - hours cut off`);
            return; // Skip to next record
          }

          // Skip OFF days (Sundays without work)
          if (record.leave_type === 'OFF' && !record.check_in && !record.check_out) {
            return; // Skip to next record
          }

          // Process records with actual check-in/check-out times
          if (record.check_in || record.check_out) {
            const hours = parseFloat(record.total_hours) || 0;

            // FIXED: Only PUBLIC HOLIDAYS get double rate (not Sundays)
            // Sundays count as regular hours towards the 195h overtime threshold
            if (record.leave_type === 'PH' || isPublicHoliday) {
              totalPHHours += hours;
              phDaysCount++;  // Track PH days for lunch deduction exclusion
              console.log(`✨ ${employee.first_name}: ${recordDate} PUBLIC HOLIDAY - ${hours}h @ double rate`);
            } else {
              // Regular hours (includes Sundays)
              totalRegularHours += hours;
              if (isSunday) {
                console.log(`📅 ${employee.first_name}: ${recordDate} SUNDAY - ${hours}h counted as regular (towards 195h threshold)`);
              }
            }

            // Add the date to the set (this automatically handles duplicates)
            if (record.date) {
              uniqueDates.add(record.date);
            }
          }
        });

        // FIXED: Working days is the count of unique dates (includes actual work days + leave days)
        const workingDays = uniqueDates.size;
        const actualWorkDays = workingDays - leaveDays; // Days with actual work (exclude leave days)

        // STEP 2: Get EMPLOYEE-SPECIFIC break_minutes from employee table
        // CRITICAL: Only deduct lunch for REGULAR work days, NOT for leave days or PH days
        // Use employee's break_minutes - if not set or null, default to 30 (not 60)
        const breakMinutes = employee.break_minutes !== null && employee.break_minutes !== undefined
          ? employee.break_minutes
          : 30;  // Default to 30 minutes if not set

        // FIXED: Only deduct lunch from regular work days (exclude PH days)
        const regularWorkDays = actualWorkDays - phDaysCount;
        const lunchDeductionHours = regularWorkDays * (breakMinutes / 60);

        console.log(`✅ ${employee.first_name}: ${records.length} records, ${workingDays} unique days (${regularWorkDays} regular + ${phDaysCount} PH + ${leaveDays} leave), ${lunchDeductionHours.toFixed(2)}h break deduction (${breakMinutes}min/day)`);

        // STEP 3: Calculate net hours
        const netHours = Math.max(0, totalRegularHours - lunchDeductionHours);

        // STEP 4: Get hourly rate
        const hourlyRate = parseFloat(employee.hourly_rate) || 0;

        // STEP 4.5: Get EMPLOYEE-SPECIFIC overtime threshold (with fallback to global setting)
        const employeeOvertimeThreshold = employee.monthly_overtime_threshold
          ? parseFloat(employee.monthly_overtime_threshold)
          : overtimeThreshold;

        if (employee.monthly_overtime_threshold) {
          console.log(`⚙️ ${employee.first_name}: Using employee-specific overtime threshold: ${employeeOvertimeThreshold}h`);
        }

        // STEP 5: Calculate base pay and tiered overtime
        let basePay = 0;
        let tier1OvertimeHours = 0;
        let tier1OvertimePay = 0;
        let tier2OvertimeHours = 0;
        let tier2OvertimePay = 0;

        if (netHours <= employeeOvertimeThreshold) {
          basePay = netHours * hourlyRate;
        } else {
          basePay = employeeOvertimeThreshold * hourlyRate;

          const overtimeHours = netHours - employeeOvertimeThreshold;
          const tier1Threshold = tier1Hours;
          
          if (overtimeHours <= tier1Threshold) {
            tier1OvertimeHours = overtimeHours;
            tier1OvertimePay = tier1OvertimeHours * tier1OvertimeRate * hourlyRate;
          } else {
            tier1OvertimeHours = tier1Threshold;
            tier1OvertimePay = tier1OvertimeHours * tier1OvertimeRate * hourlyRate;
            
            tier2OvertimeHours = overtimeHours - tier1Threshold;
            tier2OvertimePay = tier2OvertimeHours * tier2OvertimeRate * hourlyRate;
          }
        }

        // STEP 6: PH pay
        const phPay = totalPHHours * hourlyRate * holidayRate;

        // STEP 7: Gross pay before allowances
        const grossPayBeforeAllowances = basePay + tier1OvertimePay + tier2OvertimePay + phPay;

        // STEP 8: Calculate allowances with working days for per_day allowances
        const allowanceData = await this.calculateEmployeeAllowances(
          employee.id,
          startDate,
          endDate,
          company_id,
          grossPayBeforeAllowances,
          workingDays  // Pass correct working days count
        );

        // STEP 9: Gross pay is base pay ONLY (allowances shown separately)
        const grossPay = grossPayBeforeAllowances;

        // STEP 10: Calculate recurring deductions
        const deductionData = await this.calculateEmployeeDeductions(
          employee.id,
          startDate,
          endDate,
          company_id,
          grossPayBeforeAllowances  // Calculate deductions on base pay
        );

        // STEP 11: Calculate net pay = Gross + Allowances - Deductions
        const netPay = grossPay + allowanceData.totalAllowances - deductionData.totalDeductions;

        console.log(`💰 ${employee.first_name}: Gross=${grossPay.toFixed(2)}, Allowances=${allowanceData.totalAllowances.toFixed(2)}, Deductions=${deductionData.totalDeductions.toFixed(2)}, Net=${netPay.toFixed(2)} [Net = Gross + Allowances - Deductions]`);

        // CRITICAL: Build calculation_metadata object
        const metadataObject = {
          method: 'simple_enhanced_v6_ph_lunch_fix',  // FIXED: Lunch only deducted from regular days, not PH days
          break_minutes: breakMinutes,
          total_regular_hours: totalRegularHours,
          total_ph_hours: totalPHHours,
          leave_hours: leaveHours,
          leave_days: leaveDays,
          ph_days_count: phDaysCount,
          regular_work_days: regularWorkDays,
          actual_work_days: actualWorkDays,
          lunch_deduction_hours: lunchDeductionHours,
          net_hours: netHours,
          working_days: workingDays,
          total_records: records.length,
          unique_dates: Array.from(uniqueDates).sort(),
          overtime_threshold_used: employeeOvertimeThreshold,
          overtime_threshold_source: employee.monthly_overtime_threshold ? 'employee' : 'company',
          calculated_at: new Date().toISOString()
        };

        console.log(`📝 Metadata for ${employee.first_name}:`, metadataObject);

        return {
          company_id,
          period_id,
          employee_id: employee.id,
          regular_hours: netHours <= employeeOvertimeThreshold ? netHours : employeeOvertimeThreshold,
          overtime_hours: tier1OvertimeHours + tier2OvertimeHours,
          double_overtime_hours: 0,
          holiday_hours: totalPHHours,
          unpaid_leave_hours: 0,
          regular_amount: basePay,
          overtime_amount: tier1OvertimePay + tier2OvertimePay,
          double_overtime_amount: 0,
          holiday_amount: phPay,
          total_allowances: allowanceData.totalAllowances,
          allowance_details: JSON.stringify(allowanceData.allowanceDetails),
          leave_excess_deduction: 0,
          gross_pay: grossPay,
          total_deductions: deductionData.totalDeductions,
          deduction_details: JSON.stringify(deductionData.deductionDetails),
          net_pay: netPay,
          status: 'pending',
          calculation_metadata: JSON.stringify(metadataObject),  // Stringify the object
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }));

      // Insert payroll records with explicit error handling
      if (payrollRecords.length > 0) {
        console.log(`💾 Inserting ${payrollRecords.length} payroll records...`);
        
        const { data: insertedData, error: insertError } = await supabase
          .from('payroll_records')
          .insert(payrollRecords)
          .select();  // Add select to return inserted data

        if (insertError) {
          console.error('❌ Insert error:', insertError);
          throw insertError;
        }
        
        console.log('✅ Successfully inserted payroll records:', insertedData?.length || payrollRecords.length);
        
        // Verify calculation_metadata was saved
        if (insertedData && insertedData.length > 0) {
          const sample = insertedData[0];
          console.log('🔍 Sample inserted record has calculation_metadata:', !!sample.calculation_metadata);
          if (sample.calculation_metadata) {
            console.log('📦 Sample metadata:', sample.calculation_metadata);
          }
        }
      }

      return payrollRecords;
    } catch (error) {
      console.error('❌ Payroll calculation error:', error);
      throw error;
    }
  },

  async createPayrollPeriod(startDate, endDate, company_id) {
    const { data, error } = await supabase
      .from('payroll_periods')
      .insert([{
        company_id,
        start_date: startDate,
        end_date: endDate,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async calculateEmployeeDeductions(employee_id, startDate, endDate, company_id, baseGrossPay) {
    try {
      // Fetch ALL deductions (including "Always Active" ones with null dates)
      const { data: deductions, error } = await supabase
        .from('employee_deductions')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          deduction_type_id,
          deduction_types (
            id,
            name,
            is_percentage
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id);

      if (error) {
        console.error('Error fetching deductions:', error);
        return {
          totalDeductions: 0,
          deductionDetails: []
        };
      }

      let totalDeductions = 0;
      const deductionDetails = [];

      (deductions || []).forEach(deduction => {
        let isActive = false;

        // Check if this is an "Always Active" deduction (null dates)
        if (!deduction.start_date || !deduction.end_date) {
          isActive = true;
          console.log(`✅ Always Active deduction: ${deduction.deduction_types?.name}`);
        } else {
          // Period-based deduction - check if active during payroll period
          const deductionStart = moment(deduction.start_date);
          const deductionEnd = moment(deduction.end_date);
          const periodStart = moment(startDate);
          const periodEnd = moment(endDate);

          isActive = deductionStart.isSameOrBefore(periodEnd) && deductionEnd.isSameOrAfter(periodStart);
        }

        if (isActive) {
          let deductionAmount = 0;

          // Calculate deduction amount based on type
          if (deduction.deduction_types?.is_percentage) {
            deductionAmount = baseGrossPay * (deduction.amount / 100);
          } else {
            deductionAmount = deduction.amount;
          }

          totalDeductions += deductionAmount;

          deductionDetails.push({
            id: deduction.id,
            name: deduction.deduction_types?.name || 'Unknown',
            amount: deductionAmount,
            is_percentage: deduction.deduction_types?.is_percentage || false,
            original_amount: deduction.amount
          });
        }
      });

      console.log(`💰 Employee deductions: ${totalDeductions.toFixed(2)} (${deductionDetails.length} items)`);

      return {
        totalDeductions,
        deductionDetails
      };
    } catch (error) {
      console.error('Error calculating deductions:', error);
      return {
        totalDeductions: 0,
        deductionDetails: []
      };
    }
  },

  async calculateEmployeeAllowances(employee_id, startDate, endDate, company_id, baseGrossPay, workingDays) {
    try {
      const { data: allowances, error } = await supabase
        .from('employee_allowances')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          allowance_type_id,
          allowance_types (
            id,
            name,
            is_percentage,
            is_taxable,
            calculation_method
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id)
        .or(`start_date.lte.${endDate},end_date.gte.${startDate}`);

      if (error) {
        return {
          totalAllowances: 0,
          taxableAllowances: 0,
          nonTaxableAllowances: 0,
          allowanceDetails: []
        };
      }

      let totalAllowances = 0;
      let taxableAllowances = 0;
      let nonTaxableAllowances = 0;
      const allowanceDetails = [];

      (allowances || []).forEach(allowance => {
        const allowanceStart = moment(allowance.start_date);
        const allowanceEnd = moment(allowance.end_date);
        const periodStart = moment(startDate);
        const periodEnd = moment(endDate);

        const isActive = allowanceStart.isSameOrBefore(periodEnd) && allowanceEnd.isSameOrAfter(periodStart);

        if (isActive) {
          let allowanceAmount = 0;
          const calculationMethod = allowance.allowance_types?.calculation_method || 'fixed';

          // HANDLE THREE CALCULATION METHODS
          if (calculationMethod === 'percentage') {
            allowanceAmount = baseGrossPay * (allowance.amount / 100);
          } else if (calculationMethod === 'per_day') {
            allowanceAmount = allowance.amount * workingDays;
          } else {
            allowanceAmount = allowance.amount;
          }

          totalAllowances += allowanceAmount;

          if (allowance.allowance_types?.is_taxable) {
            taxableAllowances += allowanceAmount;
          } else {
            nonTaxableAllowances += allowanceAmount;
          }

          allowanceDetails.push({
            id: allowance.id,
            name: allowance.allowance_types?.name || 'Unknown',
            amount: allowanceAmount,
            calculation_method: calculationMethod,
            is_percentage: allowance.allowance_types?.is_percentage || false,
            is_taxable: allowance.allowance_types?.is_taxable || true,
            original_amount: allowance.amount,
            working_days: calculationMethod === 'per_day' ? workingDays : null
          });
        }
      });

      return {
        totalAllowances,
        taxableAllowances,
        nonTaxableAllowances,
        allowanceDetails
      };
    } catch (error) {
      console.error('Error calculating allowances:', error);
      return {
        totalAllowances: 0,
        taxableAllowances: 0,
        nonTaxableAllowances: 0,
        allowanceDetails: []
      };
    }
  },

  async recalculatePayrollPeriod(period_id, company_id) {
    console.log('🔄 Starting SIMPLE payroll recalculation for period:', period_id);

    try {
      // Get the existing period dates
      const { data: period, error: periodError } = await supabase
        .from('payroll_periods')
        .select('start_date, end_date')
        .eq('id', period_id)
        .eq('company_id', company_id)
        .single();

      if (periodError) throw periodError;
      if (!period) throw new Error('Payroll period not found');

      const { start_date: startDate, end_date: endDate } = period;
      console.log(`📅 Period dates: ${startDate} to ${endDate}`);

      // Delete existing payroll records for this period
      const { data: deletedRecords, error: deleteError } = await supabase
        .from('payroll_records')
        .delete()
        .eq('period_id', period_id)
        .eq('company_id', company_id)
        .select();

      if (deleteError) throw deleteError;
      console.log(`🗑️ Deleted ${deletedRecords?.length || 0} existing records`);

      // Fetch company settings
      const { data: companySettings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', company_id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      const overtimeThreshold = companySettings?.payroll_settings?.monthly_overtime_threshold || 195;
      const tier1OvertimeRate = companySettings?.payroll_settings?.overtime_rate || 1.5;
      const tier2OvertimeRate = companySettings?.payroll_settings?.double_overtime_rate || 2.0;
      const tier1Hours = companySettings?.payroll_settings?.overtime_tier_1_hours || 5;
      const holidayRate = companySettings?.payroll_settings?.holiday_rate || 2.0;

      // Fetch public holidays
      const { data: publicHolidays, error: holidaysError } = await supabase
        .from('holidays')
        .select('date, is_recurring, name')
        .eq('company_id', company_id);

      if (holidaysError) {
        console.error('Warning: Error fetching holidays:', holidaysError);
      }

      const isPublicHolidayDate = (checkDate) => {
        return (publicHolidays || []).some(holiday => {
          const holidayDate = moment(holiday.date);
          const targetDate = moment(checkDate);
          if (holiday.is_recurring) {
            return holidayDate.month() === targetDate.month() &&
                   holidayDate.date() === targetDate.date();
          } else {
            return holidayDate.format('YYYY-MM-DD') === targetDate.format('YYYY-MM-DD');
          }
        });
      };

      // Get attendance data
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('timesheet_daily_records')
        .select(`
          *,
          employees (
            id, first_name, last_name, employee_number, position, department, hourly_rate, break_minutes
          )
        `)
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (attendanceError) throw attendanceError;

      const employeeIds = [...new Set(attendanceData.map(record => record.employee_id))];

      const { data: employees, error: employeeError } = await supabase
        .from('employees')
        .select('*, monthly_overtime_threshold')
        .in('id', employeeIds);

      if (employeeError) throw employeeError;

      // Group attendance by employee
      const employeeAttendance = {};
      attendanceData.forEach(record => {
        if (!employeeAttendance[record.employee_id]) {
          employeeAttendance[record.employee_id] = [];
        }
        employeeAttendance[record.employee_id].push(record);
      });

      // Calculate payroll for each employee (same logic as calculatePayrollSimple)
      const payrollRecords = await Promise.all(employees.map(async (employee) => {
        const records = employeeAttendance[employee.id] || [];

        let totalRegularHours = 0;
        let totalPHHours = 0;
        let leaveHours = 0;
        let leaveDays = 0;
        let phDaysCount = 0;
        const uniqueDates = new Set();

        records.forEach(record => {
          const recordDate = record.date;
          const dayOfWeek = moment(recordDate).day();
          const isPublicHoliday = isPublicHolidayDate(recordDate);
          const isSunday = dayOfWeek === 0;

          if (record.leave_type === 'SICK LEAVE' || record.leave_type === 'LOCAL LEAVE') {
            const leaveHoursPerDay = 8;
            totalRegularHours += leaveHoursPerDay;
            leaveHours += leaveHoursPerDay;
            leaveDays++;
            uniqueDates.add(recordDate);
            return;
          }

          if (record.leave_type === 'ABSENT' || record.status === 'absent') {
            return;
          }

          if (record.leave_type === 'OFF' && !record.check_in && !record.check_out) {
            return;
          }

          if (record.check_in || record.check_out) {
            const hours = parseFloat(record.total_hours) || 0;

            if (record.leave_type === 'PH' || isPublicHoliday) {
              totalPHHours += hours;
              phDaysCount++;
            } else {
              totalRegularHours += hours;
            }

            if (record.date) {
              uniqueDates.add(record.date);
            }
          }
        });

        const workingDays = uniqueDates.size;
        const actualWorkDays = workingDays - leaveDays;

        const breakMinutes = employee.break_minutes !== null && employee.break_minutes !== undefined
          ? employee.break_minutes
          : 30;

        const regularWorkDays = actualWorkDays - phDaysCount;
        const lunchDeductionHours = regularWorkDays * (breakMinutes / 60);

        const netHours = Math.max(0, totalRegularHours - lunchDeductionHours);
        const hourlyRate = parseFloat(employee.hourly_rate) || 0;

        const employeeOvertimeThreshold = employee.monthly_overtime_threshold
          ? parseFloat(employee.monthly_overtime_threshold)
          : overtimeThreshold;

        let basePay = 0;
        let tier1OvertimeHours = 0;
        let tier1OvertimePay = 0;
        let tier2OvertimeHours = 0;
        let tier2OvertimePay = 0;

        if (netHours <= employeeOvertimeThreshold) {
          basePay = netHours * hourlyRate;
        } else {
          basePay = employeeOvertimeThreshold * hourlyRate;
          const overtimeHoursCalc = netHours - employeeOvertimeThreshold;
          const tier1Threshold = tier1Hours;

          if (overtimeHoursCalc <= tier1Threshold) {
            tier1OvertimeHours = overtimeHoursCalc;
            tier1OvertimePay = tier1OvertimeHours * tier1OvertimeRate * hourlyRate;
          } else {
            tier1OvertimeHours = tier1Threshold;
            tier1OvertimePay = tier1OvertimeHours * tier1OvertimeRate * hourlyRate;
            tier2OvertimeHours = overtimeHoursCalc - tier1Threshold;
            tier2OvertimePay = tier2OvertimeHours * tier2OvertimeRate * hourlyRate;
          }
        }

        const phPay = totalPHHours * hourlyRate * holidayRate;
        const grossPayBeforeAllowances = basePay + tier1OvertimePay + tier2OvertimePay + phPay;

        const allowanceData = await this.calculateEmployeeAllowances(
          employee.id, startDate, endDate, company_id, grossPayBeforeAllowances, workingDays
        );

        const grossPay = grossPayBeforeAllowances;

        const deductionData = await this.calculateEmployeeDeductions(
          employee.id, startDate, endDate, company_id, grossPayBeforeAllowances
        );

        const netPay = grossPay + allowanceData.totalAllowances - deductionData.totalDeductions;

        console.log(`🔄 ${employee.first_name}: Gross=${grossPay.toFixed(2)}, Allowances=${allowanceData.totalAllowances.toFixed(2)}, Deductions=${deductionData.totalDeductions.toFixed(2)}, Net=${netPay.toFixed(2)}`);

        const metadataObject = {
          method: 'simple_enhanced_v6_recalculated',
          break_minutes: breakMinutes,
          total_regular_hours: totalRegularHours,
          total_ph_hours: totalPHHours,
          leave_hours: leaveHours,
          leave_days: leaveDays,
          ph_days_count: phDaysCount,
          regular_work_days: regularWorkDays,
          actual_work_days: actualWorkDays,
          lunch_deduction_hours: lunchDeductionHours,
          net_hours: netHours,
          working_days: workingDays,
          total_records: records.length,
          unique_dates: Array.from(uniqueDates).sort(),
          overtime_threshold_used: employeeOvertimeThreshold,
          overtime_threshold_source: employee.monthly_overtime_threshold ? 'employee' : 'company',
          recalculated_at: new Date().toISOString()
        };

        return {
          company_id,
          period_id,
          employee_id: employee.id,
          regular_hours: netHours <= employeeOvertimeThreshold ? netHours : employeeOvertimeThreshold,
          overtime_hours: tier1OvertimeHours + tier2OvertimeHours,
          double_overtime_hours: 0,
          holiday_hours: totalPHHours,
          unpaid_leave_hours: 0,
          regular_amount: basePay,
          overtime_amount: tier1OvertimePay + tier2OvertimePay,
          double_overtime_amount: 0,
          holiday_amount: phPay,
          total_allowances: allowanceData.totalAllowances,
          allowance_details: JSON.stringify(allowanceData.allowanceDetails),
          leave_excess_deduction: 0,
          gross_pay: grossPay,
          total_deductions: deductionData.totalDeductions,
          deduction_details: JSON.stringify(deductionData.deductionDetails),
          net_pay: netPay,
          status: 'pending',
          calculation_metadata: JSON.stringify(metadataObject),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }));

      // Insert new payroll records
      if (payrollRecords.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('payroll_records')
          .insert(payrollRecords)
          .select();

        if (insertError) throw insertError;
        console.log(`✅ Recalculated and inserted ${insertedData?.length || payrollRecords.length} payroll records`);
      }

      return payrollRecords;
    } catch (error) {
      console.error('Error recalculating payroll:', error);
      throw error;
    }
  },

  async getPayrollPeriods(company_id) {
    const { data, error } = await supabase
      .from('payroll_periods')
      .select('*')
      .eq('company_id', company_id)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getPayrollRecords(period_id, company_id) {
    try {
      console.log('🔍 Fetching payroll records for period:', period_id);
      
      // FIXED: Explicitly select calculation_metadata
      const { data, error } = await supabase
        .from('payroll_records')
        .select(`
          id,
          company_id,
          period_id,
          employee_id,
          regular_hours,
          overtime_hours,
          double_overtime_hours,
          holiday_hours,
          unpaid_leave_hours,
          regular_amount,
          overtime_amount,
          double_overtime_amount,
          holiday_amount,
          total_allowances,
          allowance_details,
          leave_excess_deduction,
          gross_pay,
          total_deductions,
          deduction_details,
          net_pay,
          status,
          leave_usage,
          calculation_metadata,
          created_at,
          updated_at,
          employees (
            id,
            first_name,
            last_name,
            department,
            hourly_rate,
            break_minutes,
            employee_number,
            position
          )
        `)
        .eq('period_id', period_id)
        .eq('company_id', company_id);

      if (error) {
        console.error('❌ Error fetching payroll records:', error);
        throw error;
      }

      console.log(`✅ Fetched ${data?.length || 0} payroll records`);
      
      // Diagnostic: Check if calculation_metadata exists in the first record
      if (data && data.length > 0) {
        const sample = data[0];
        console.log('🔍 Sample record structure:', Object.keys(sample));
        console.log('📦 Has calculation_metadata:', !!sample.calculation_metadata);
        if (sample.calculation_metadata) {
          try {
            const parsed = typeof sample.calculation_metadata === 'string' 
              ? JSON.parse(sample.calculation_metadata) 
              : sample.calculation_metadata;
            console.log('✅ Parsed metadata:', parsed);
          } catch (e) {
            console.error('❌ Failed to parse metadata:', e);
          }
        } else {
          console.warn('⚠️ No calculation_metadata in records. This may be an old payroll. Delete and recreate.');
        }
      }

      return data;
    } catch (error) {
      console.error('❌ Error getting payroll records:', error);
      throw new Error('Failed to load payroll records: ' + error.message);
    }
  },

  async processDeductions(payroll_record_id, deductions, company_id) {
    try {
      const { data: record, error: recordError } = await supabase
        .from('payroll_records')
        .select('*')
        .eq('id', payroll_record_id)
        .eq('company_id', company_id)
        .single();

      if (recordError) throw recordError;

      const additionalDeductions = deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
      const totalDeductions = additionalDeductions;
      const netPay = record.gross_pay - totalDeductions;

      const { error: updateError } = await supabase
        .from('payroll_records')
        .update({
          total_deductions: totalDeductions,
          net_pay: netPay,
          updated_at: new Date().toISOString()
        })
        .eq('id', payroll_record_id)
        .eq('company_id', company_id);

      if (updateError) throw updateError;

      if (deductions.length > 0) {
        const { error: deductionsError } = await supabase
          .from('employee_deductions')
          .insert(deductions.map(d => ({
            ...d,
            company_id,
            payroll_record_id,
            created_at: new Date().toISOString()
          })));

        if (deductionsError) throw deductionsError;
      }

      return {
        totalDeductions,
        netPay
      };
    } catch (error) {
      console.error('Error processing deductions:', error);
      throw error;
    }
  }
};

export default payrollServiceSimple;